// Bottom review bar: the focused place to sign off the SELECTED layer. Each dimension
// (3D, then 2D) is its own block: label + last-updated + Sign off / Flag / Exclude, then
// a comment THREAD beneath it. Flagging a dimension opens its comment box; each comment is
// stamped with the user + time and leaves a fresh box for the next person. A per-comment
// done-tick marks individual points addressed. The tree just shows status; this commits it.
import { api, type AuditEntry, type MarkupOut, type RegisterNode, type Review, type ReviewState } from "../api";
import { $activeProjectId, $user } from "../store";
import { expectedDims, dimState, dimNotes } from "./layerTree";

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

function auditLine(e: AuditEntry): string {
  const who = e.actor ? `${e.actor}` : "someone";
  const when = fmtTime(e.at);
  if (e.entity === "review_status") {
    const dim = (e.field || "").replace("status_", "").toUpperCase();
    return `${dim} marked "${e.new}" by ${who} · ${when}`;
  }
  if (e.entity === "markup") {
    const verb = e.action === "create" ? "commented" : e.action === "delete" ? "deleted a comment" : "updated a comment";
    return `${who} ${verb} · ${when}`;
  }
  return `${e.entity} ${e.action} by ${who} · ${when}`;
}

export interface ReviewBarHandlers {
  review: (node: RegisterNode, dim: "2d" | "3d", status: ReviewState, notes: string | null) => Promise<Review | null>;
  comment: (node: RegisterNode) => void;
  selectNode: (node: RegisterNode) => void; // select + fly + show
  nextUnreviewed: (fromNode: RegisterNode) => RegisterNode | null;
}

export class ReviewBar {
  private el: HTMLElement;
  private node: RegisterNode | null = null;
  private h: ReviewBarHandlers | null = null;
  private comments: MarkupOut[] = [];
  private onCommentsChanged: () => void = () => {};

  constructor() {
    this.el = document.createElement("div");
    this.el.className = "review-bar";
    // Live inside the map container so it's bounded by the map area (never overlaps the
    // resizable sidebar) and centres there instead of stretching the whole screen.
    (document.getElementById("cesium-container") ?? document.body).append(this.el);
  }

  setHandlers(h: ReviewBarHandlers): void {
    this.h = h;
  }

  /** Called after a comment is added/resolved so the host can refresh the markup list. */
  setOnCommentsChanged(fn: () => void): void {
    this.onCommentsChanged = fn;
  }

  show(node: RegisterNode): void {
    // Any node with expected data is reviewable - including group/parent nodes, so the whole
    // layer (Native Title Determination, Comms Network) can be commented on, not just parts.
    if (expectedDims(node).length === 0) return this.hide();
    const changed = this.node?.id !== node.id;
    this.node = node;
    if (changed) this.comments = [];
    this.render();
    this.el.classList.add("show");
    if (changed) {
      this.el.classList.remove("rb-pop");
      void this.el.offsetWidth; // reflow to restart the pulse
      this.el.classList.add("rb-pop");
      void this.loadComments();
    }
  }

  hide(): void {
    this.node = null;
    this.el.classList.remove("show");
  }

  private async loadComments(): Promise<void> {
    const pid = $activeProjectId.get();
    if (pid == null || !this.node) {
      this.comments = [];
      this.render();
      return;
    }
    try {
      const { comments } = await api.getItemComments(pid, this.node.id);
      this.comments = comments;
    } catch {
      this.comments = [];
    }
    this.render();
  }

  private async act(dim: "2d" | "3d", status: ReviewState, notes: string | null): Promise<void> {
    if (!this.node || !this.h) return;
    const r = await this.h.review(this.node, dim, status, notes);
    if (r) {
      this.node.review = r;
      this.render();
    }
  }

  private async sendComment(dim: "2D" | "3D", text: string): Promise<void> {
    const pid = $activeProjectId.get();
    if (pid == null || !this.node) return;
    await api.createMarkup(pid, {
      type: "note", comment: text, register_item_id: this.node.id,
      dimension: dim, author: $user.get() || null,
    });
    // If this dimension is flagged, mirror the latest comment into the tree's red note.
    if (dimState(this.node, dim) === "needs_work" && this.h) {
      const r = await this.h.review(this.node, dim.toLowerCase() as "2d" | "3d", "needs_work", text);
      if (r) this.node.review = r;
    }
    await this.loadComments();
    this.onCommentsChanged();
    this.el.querySelector<HTMLInputElement>(`.rb-dim[data-dim="${dim}"] .rb-cmt-input`)?.focus();
  }

  private async toggleCommentDone(c: MarkupOut): Promise<void> {
    const pid = $activeProjectId.get();
    if (pid == null) return;
    await api.patchMarkup(pid, c.id, { resolved: !c.resolved, actor: $user.get() || null });
    await this.loadComments();
    this.onCommentsChanged();
  }

  /** Public: re-pull this layer's comments (so a resolve/edit made elsewhere shows here too). */
  async refreshComments(): Promise<void> {
    if (this.node) await this.loadComments();
  }

  /** Inline-edit a comment's text in place. Saves on Enter/blur, reverts on Esc. */
  private beginEdit(c: MarkupOut, textEl: HTMLElement): void {
    const input = document.createElement("input");
    input.className = "rb-cmt-editbox";
    input.value = c.comment || "";
    textEl.replaceWith(input);
    input.focus();
    input.select();
    let finished = false;
    const commit = async (save: boolean): Promise<void> => {
      if (finished) return;
      finished = true;
      const pid = $activeProjectId.get();
      const text = input.value.trim();
      if (save && pid != null && text && text !== (c.comment || "")) {
        await api.patchMarkup(pid, c.id, { comment: text, actor: $user.get() || null });
        await this.loadComments();
        this.onCommentsChanged();
      } else {
        this.render(); // revert to the rendered comment
      }
    };
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void commit(true);
      else if (e.key === "Escape") void commit(false);
    });
    input.addEventListener("blur", () => void commit(true));
  }

  private dimRow(dim: "2D" | "3D"): HTMLElement {
    const node = this.node!;
    const key = dim.toLowerCase() as "2d" | "3d";
    const state = dimState(node, dim);
    const wrap = document.createElement("div");
    wrap.className = "rb-dim";
    wrap.dataset.dim = dim;

    const top = document.createElement("div");
    top.className = "rb-dim-top";
    top.innerHTML = `<span class="rb-dimlab dim-${key}">${dim}</span>`;

    const updated = node.assets
      .filter((a) => a.dimension === dim)
      .map((a) => a.updated)
      .filter(Boolean)
      .sort()
      .pop();
    if (updated) {
      const u = document.createElement("span");
      u.className = "rb-updated";
      u.textContent = `updated ${fmtTime(updated)}`;
      top.append(u);
    }

    const mk = (label: string, cls: string, active: boolean, onClick: () => void): HTMLButtonElement => {
      const b = document.createElement("button");
      b.className = `rb-btn ${cls}${active ? " active" : ""}`;
      b.textContent = label;
      b.addEventListener("click", onClick);
      return b;
    };

    top.append(
      mk("✓ Sign off", "rb-approve", state === "approved", () =>
        void this.act(key, state === "approved" ? "not_started" : "approved", null),
      ),
      mk("✗ Flag", "rb-flag", state === "needs_work", () => {
        if (state === "needs_work") return void this.act(key, "not_started", null);
        // Flagging opens the comment box for this dimension.
        void this.act(key, "needs_work", dimNotes(node, dim) || "").then(() =>
          this.el.querySelector<HTMLInputElement>(`.rb-dim[data-dim="${dim}"] .rb-cmt-input`)?.focus(),
        );
      }),
      mk("⊘", "rb-exclude", state === "excluded", () =>
        void this.act(key, state === "excluded" ? "not_started" : "excluded", null),
      ),
    );

    const r = node.review;
    const reviewedAt = dim === "2D" ? r?.reviewed_2d_at : r?.reviewed_3d_at;
    if (state !== "not_started" && reviewedAt) {
      const s = document.createElement("span");
      s.className = "rb-signoff";
      s.textContent = `${r?.reviewer ? r.reviewer + " · " : ""}${fmtTime(reviewedAt)}`;
      top.append(s);
    }
    wrap.append(top);

    // --- comment thread for this dimension ---
    const dimComments = this.comments.filter((c) => c.dimension === dim);
    const active = dimComments.filter((c) => !c.resolved);
    const resolved = dimComments.filter((c) => c.resolved);
    const thread = document.createElement("div");
    thread.className = "rb-thread";

    const renderCmt = (c: MarkupOut, into: HTMLElement) => {
      const row = document.createElement("div");
      row.className = "rb-cmt" + (c.resolved ? " resolved" : "");
      const meta = document.createElement("span");
      meta.className = "rb-cmt-meta";
      meta.textContent =
        `${c.author || "?"} · ${fmtTime(c.created_at)}` +
        (c.resolved ? ` · done ${fmtTime(c.resolved_at)}` : "");
      const textEl = document.createElement("span");
      textEl.className = "rb-cmt-text";
      textEl.textContent = c.comment || "";
      row.append(meta, textEl);

      const edit = document.createElement("button");
      edit.className = "rb-cmt-edit";
      edit.textContent = "✎";
      edit.title = "Edit this comment";
      edit.addEventListener("click", () => this.beginEdit(c, textEl));

      const done = document.createElement("button");
      done.className = "rb-cmt-done" + (c.resolved ? " active" : "");
      done.textContent = "✓";
      done.title = c.resolved ? "Reopen this comment" : "Mark this comment done";
      done.addEventListener("click", () => void this.toggleCommentDone(c));
      row.append(edit, done);
      into.append(row);
    };

    // Only OPEN comments clutter the active thread; resolved ones tuck into a tidy log.
    for (const c of active) renderCmt(c, thread);
    if (resolved.length) {
      const logToggle = document.createElement("button");
      logToggle.className = "rb-log-toggle";
      logToggle.textContent = `✓ ${resolved.length} resolved`;
      const log = document.createElement("div");
      log.className = "rb-log";
      log.style.display = "none";
      for (const c of resolved) renderCmt(c, log);
      logToggle.addEventListener("click", () => {
        const open = log.style.display === "none";
        log.style.display = open ? "block" : "none";
        logToggle.classList.toggle("open", open);
      });
      thread.append(logToggle, log);
    }

    // Comment box: greyed unless the dimension is flagged OR a thread already exists.
    const canComment = state === "needs_work" || dimComments.length > 0;
    const input = document.createElement("input");
    input.className = "rb-cmt-input";
    input.disabled = !canComment;
    input.placeholder = canComment ? `Add a ${dim} comment…` : `Flag ${dim} to start a comment`;
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && input.value.trim()) {
        const text = input.value.trim();
        input.value = "";
        void this.sendComment(dim, text);
      }
    });
    thread.append(input);
    wrap.append(thread);
    return wrap;
  }

  private async loadHistory(listEl: HTMLElement): Promise<void> {
    const pid = $activeProjectId.get();
    if (pid == null || !this.node) return;
    listEl.innerHTML = '<div class="rb-hist-empty">Loading…</div>';
    try {
      const { audit } = await api.getAudit(pid, this.node.id);
      if (!audit.length) {
        listEl.innerHTML = '<div class="rb-hist-empty">No history yet for this layer.</div>';
        return;
      }
      listEl.innerHTML = "";
      for (const e of audit) {
        const row = document.createElement("div");
        row.className = "rb-hist-row";
        row.textContent = auditLine(e);
        listEl.append(row);
      }
    } catch {
      listEl.innerHTML = '<div class="rb-hist-empty">Could not load history.</div>';
    }
  }

  private render(): void {
    const node = this.node;
    if (!node || !this.h) return;
    this.el.innerHTML = "";

    const head = document.createElement("div");
    head.className = "rb-head";
    head.innerHTML = `<span class="rb-name">${esc(node.layer_name ?? "(layer)")}</span>`;
    const close = document.createElement("button");
    close.className = "rb-close";
    close.textContent = "×";
    close.title = "Close review bar";
    close.addEventListener("click", () => this.hide());
    head.append(close);

    const dims = document.createElement("div");
    dims.className = "rb-dims";
    const expected = expectedDims(node).slice().sort((a) => (a === "3D" ? -1 : 1)); // 3D first
    if (expected.length === 0) {
      dims.innerHTML = '<span class="rb-empty">No 2D/3D data to review on this layer.</span>';
    } else {
      for (const d of expected) dims.append(this.dimRow(d));
    }

    const actions = document.createElement("div");
    actions.className = "rb-actions";
    const next = document.createElement("button");
    next.className = "rb-action rb-next";
    next.textContent = "Next to review ›";
    next.title = "Jump to the next layer with a dimension still not reviewed";
    next.addEventListener("click", () => {
      const n = this.h!.nextUnreviewed(node);
      if (n) this.h!.selectNode(n);
    });

    // History log for this layer (collapsed; loads on first open).
    const hist = document.createElement("div");
    hist.className = "rb-history";
    const histList = document.createElement("div");
    histList.className = "rb-hist-list";
    histList.style.display = "none";
    const histBtn = document.createElement("button");
    histBtn.className = "rb-hist-toggle";
    histBtn.textContent = "▸ History";
    let open = false;
    histBtn.addEventListener("click", () => {
      open = !open;
      histBtn.textContent = `${open ? "▾" : "▸"} History`;
      histList.style.display = open ? "block" : "none";
      if (open) void this.loadHistory(histList);
    });
    hist.append(histBtn, histList);
    actions.append(hist, next);

    this.el.append(head, dims, actions);
  }
}
